<template>
    <div class="full-height">
        <div
            class="row justify-between items-center bg-primary q-px-md q-py-sm"
        >
            <div>
                <q-btn-group>
                    <q-btn
                        @click="isTableMode = true"
                        :color="isTableMode ? 'primary' : 'white'"
                        :text-color="isTableMode ? 'white' : 'primary'"
                        class="toggle-report-view-button"
                        icon="fas fa-table"
                        size="10px"
                        :outline="!isTableMode"
                    >
                        <q-tooltip>
                            {{ $t('Switch to table view') }}
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        @click="isTableMode = false"
                        :color="isTableMode ? 'white' : 'primary'"
                        :text-color="isTableMode ? 'primary' : 'white'"
                        class="toggle-report-view-button"
                        icon="fas fa-chart-bar"
                        size="10px"
                        :outline="isTableMode"
                    >
                        <q-tooltip>
                            {{ $t('Switch to chart view') }}
                        </q-tooltip>
                    </q-btn>
                </q-btn-group>
            </div>
            <div v-if="isTableMode">
                <q-checkbox
                    v-model="showColumns.quality"
                    :label="$t('Quality')"
                    class="q-mr-md text-white"
                    dark
                ></q-checkbox>
                <q-checkbox
                    v-model="showColumns.performance"
                    :label="$t('Performance')"
                    class="q-mr-sm text-white"
                    dark
                ></q-checkbox>
                <q-checkbox
                    v-model="showColumns.availability"
                    :label="$t('Availability')"
                    class="q-mr-lg text-white"
                    dark
                ></q-checkbox>
                <q-btn-group flat v-if="!$q.platform.is.mobile">
                    <div>
                        <q-tooltip
                            anchor="top middle"
                            self="bottom middle"
                            :offset="[10, 10]"
                        >
                            {{
                                $t(
                                    'This is a KPI report organized in the same format as the LEAN app, broken down into SKUs with subtotals for rows for assets and a facility total.'
                                )
                            }}
                        </q-tooltip>
                        <q-btn-dropdown
                            id="report-data-dropdown"
                            text-color="white"
                            icon="far fa-file-arrow-down"
                            :label="$t('Report')"
                            class="download-file-buttons"
                            size="12px"
                            flat
                        >
                            <div class="q-pa-md" style="max-width: 400px">
                                <q-btn-group spread unelevated>
                                    <q-btn
                                        id="csv-button"
                                        label="CSV"
                                        color="accent"
                                        @click="selectedFileType = 'CSV'"
                                        :outline="selectedFileType !== 'CSV'"
                                    />
                                    <q-btn
                                        id="xls-button"
                                        label="XLS"
                                        color="accent"
                                        @click="selectedFileType = 'XLS'"
                                        :outline="selectedFileType !== 'XLS'"
                                    />
                                </q-btn-group>

                                <div class="q-py-md">
                                    <q-checkbox
                                        id="subtotals-checkbox"
                                        class="full-width"
                                        v-model="includeSubtotals"
                                        :label="
                                            $t(
                                                'Include subtotals, across all products, including the subtotals per shift'
                                            )
                                        "
                                        color="accent"
                                    />
                                    <q-checkbox
                                        id="shifts-checkbox"
                                        class="full-width q-mt-md"
                                        v-model="includeShifts"
                                        :label="$t('Break down by shift')"
                                        color="accent"
                                    />
                                </div>
                                <div class="row justify-end">
                                    <q-btn
                                        id="cancel-report-button"
                                        @click="selectedFileType = 'CSV'"
                                        :label="$t('Cancel')"
                                        class="q-mr-md"
                                        color="accent"
                                        v-close-popup
                                        outline
                                    />
                                    <q-btn
                                        id="download-report-button"
                                        @click="
                                            exportReportData(selectedFileType)
                                        "
                                        label="Download"
                                        color="accent"
                                        :loading="isDownloadingReportData"
                                    />
                                </div>
                            </div>
                        </q-btn-dropdown>
                    </div>
                </q-btn-group>
            </div>
        </div>

        <!-- Table mode -->
        <performance-report-table
            v-if="isTableMode"
            :report="report"
            :visibleColumns="visibleColumns"
            :showColumns="showColumns"
            :facilityId="facilityId"
        />

        <!-- Dashboard mode -->
        <div v-else class="report-container">
            <performance-dashboard :report="report"></performance-dashboard>
        </div>
    </div>
</template>

<script>
import { getLocaleFormat } from '@models/timeformats.js';
import FileExporter from '@utils/FileExporter.js';
import { logFileDownloadMessage } from '@utils/logMessagesForE2ETesting.js';
import { mapGetters } from 'vuex';
import PerformanceDashboard from './Dashboard/PerformanceDashboard.vue';
import {
    buildPerformanceReport,
    buildTable,
    getReportDataByShift,
    getReportDataFileRows
} from './performanceReport.js';
import PerformanceReportTable from './PerformanceReportTable.vue';
import { mapState } from 'pinia';
import { useReportsStore } from '@stores/reports.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'PerformanceReport',
    components: { PerformanceDashboard, PerformanceReportTable },
    props: {
        records: {
            required: true,
            validator(val) {
                return Array.isArray(val) || val === null;
            }
        },
        facilityId: {
            type: Number,
            required: true
        },
        selectedAssets: {
            type: Set,
            required: true
        },
        selectedShifts: {
            type: Set,
            required: true
        },
        shiftIdToNames: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            // column type visibility
            showColumns: {
                availability: true,
                performance: false,
                quality: false
            },
            // button loading animation
            isDownloadingReportData: false,

            // report/table view toggler
            isTableMode: true,
            // report export options
            selectedFileType: 'XLS',
            includeSubtotals: true,
            includeShifts: true
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset',
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Build report data of current interval
         * @return {Object} report data containing table data and date interval
         */
        report() {
            const tableData = buildTable(this.reportData, 'totalDowntime');
            // sort the rows for the array.
            tableData.rows.sort(function (a, b) {
                let firstAssetName = a.name.toLowerCase();
                let secondAssetName = b.name.toLowerCase();
                return firstAssetName.localeCompare(secondAssetName);
            });
            return tableData;
        },
        /**
         * All possible filters for this report
         * @return {Object} all possible filters for this report
         */
        filters() {
            return {
                assetIds: this.selectedAssets,
                shiftIds: this.selectedShifts
            };
        },
        /**
         * Extract facility name from facility id
         * @return {String} facility name
         */
        facilityName() {
            return this.getFacility(this.facilityId)?.name;
        },
        /**
         * Get local timezone
         * @return {String} local timezone
         */
        localTimezone() {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
                return timezone;
            }
            return 'UTC';
        },
        /**
         * Get the unit system used by the facility
         * @return {String} unit system
         */
        unitSystem() {
            return this.getFacility(this.facilityId)?.unit_system;
        },
        /**
         * Get the visible columns based on the active column types.
         * @return {String[]}
         */
        visibleColumns() {
            // always show asset name + primary columns
            const visibleColumns = [
                'asset_name',
                'availability',
                'performance',
                'quality',
                'oee',
                'teep'
            ];

            // if column type is set to be visible, show all secondary columns of that column type
            if (this.showColumns.availability) {
                visibleColumns.push(
                    'running_duration',
                    'changeover_duration',
                    'downtime_duration',
                    'availability_downtime_duration',
                    'performance_downtime_duration',
                    'unplanned_downtime',
                    'planned_downtime'
                );
            }
            if (this.showColumns.performance) {
                visibleColumns.push('actual_run_rate', 'planned_rate');
            }
            if (this.showColumns.quality) {
                visibleColumns.push(
                    'total_production_count',
                    'rejected_production_count',
                    'net_production_count',
                    'production_quantity',
                    'rejection_quantity',
                    'net_quantity'
                );
            }
            return visibleColumns;
        },
        /**
         * Get the file rows for the report data for file exporting
         * @return {Array} file rows for exporting
         */
        reportDataFileRows() {
            return getReportDataFileRows(
                this.report,
                this.unitSystem,
                this.includeSubtotals,
                this.facilityName,
                this.formatDate(this.getDateRange.start),
                this.formatDate(this.getDateRange.end),
                this.shiftIdToNames
            );
        },
        /**
         * Get volume unit, based on the unit system
         * @return {String} volume unit
         */
        volumeUnit() {
            return this.unitSystem === 'imperial' ? 'gal' : 'L';
        },
        /**
         * Get weight unit, based on the unit system
         * @return {String} weight unit
         */
        weightUnit() {
            return this.unitSystem === 'imperial' ? 'lb' : 'kg';
        },
        /**
         * Get the report data by shift for file exporting
         * @return {Array} report data by shift for exporting
         */
        reportDataByShift() {
            const shiftsReport = getReportDataByShift(
                this.reportData,
                this.unitSystem,
                this.includeSubtotals,
                this.facilityName,
                this.formatDate(this.getDateRange.start),
                this.formatDate(this.getDateRange.end),
                this.shiftIdToNames
            );

            return shiftsReport;
        },
        /**
         * Generates the export file name based on the facility name,
         * date range, and optional flags for shifts and subtotals.
         * @returns {string} The generated file name in the format:
         * "{facilityName}_performance_{shift_}{subtotals_}{start_date}_{end_date}"
         */
        getExportFileName() {
            const facilityName = this.facilityName.toLowerCase();
            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            let filename = `${facilityName}_performance_`;
            if (this.includeShifts) {
                filename += 'shift_';
            }

            if (this.includeSubtotals) {
                filename += 'subtotals_';
            }
            return filename + `${start_date}_${end_date}`;
        },
        /**
         * Get the report data for the current interval
         * @return {Array} report data for the current interval
         */
        reportData() {
            return buildPerformanceReport(this.records, this.filters);
        }
    },
    methods: {
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Export aggregated report data to a file
         * @param {String} fileType - file type to export
         */
        exportReportData(fileType) {
            const fileHeaders = [
                this.$t('Facility'),
                this.$t('Asset'),
                this.$t('SKU'),
                this.$t('Product'),
                this.$t('Shift'),
                this.$t('Total production (count)'),
                this.$t('Total quantity ({units})', {
                    units: this.volumeUnit
                }),
                this.$t('Total quantity ({units})', {
                    units: this.weightUnit
                }),
                this.$t('Rejected production (count)'),
                this.$t('Reject quantity ({units})', {
                    units: this.volumeUnit
                }),
                this.$t('Reject quantity ({units})', {
                    units: this.weightUnit
                }),
                this.$t('Net production (count)'),
                this.$t('Net quantity ({units})', {
                    units: this.volumeUnit
                }),
                this.$t('Net quantity ({units})', {
                    units: this.weightUnit
                }),
                this.$t('Quality [%]'),
                this.$t('Actual rate (#/min)'),
                this.$t('Max run rate (#/min)'),
                this.$t('Performance [%]'),
                this.$t('Running (min)'),
                this.$t('Switch (min)'),
                this.$t('Total downtime (min)'),
                this.$t('Availability losses (min)'),
                this.$t('Performance losses (min)'),
                this.$t('Unplanned downtime (min)'),
                this.$t('Planned downtime (min)'),
                this.$t('Availability [%]'),
                this.$t('OEE [%]'),
                this.$t('Report Start Date').concat(` [${this.localTimezone}]`),
                this.$t('Report End Date').concat(` [${this.localTimezone}]`)
            ];

            // Begin loading
            this.isDownloadingReportData = true;

            try {
                const fileExporter = new FileExporter({
                    headers: fileHeaders
                });

                if (fileType === 'CSV') {
                    fileExporter.exportCSV({
                        filename: this.getExportFileName,
                        rows: this.includeShifts
                            ? this.reportDataByShift
                            : this.reportDataFileRows
                    });
                } else if (fileType === 'XLS') {
                    fileExporter.exportXLS({
                        filename: this.getExportFileName,
                        sheetName: 'Performance Report',
                        rows: this.includeShifts
                            ? this.reportDataByShift
                            : this.reportDataFileRows
                    });
                }
                // log message for E2E testing
                // The report filter is a string that contains the options selected for the report
                const reportFilter = [
                    this.includeShifts && 'Shifts',
                    this.includeSubtotals && 'Subtotals'
                ]
                    .filter(Boolean)
                    .join('_');
                logFileDownloadMessage(fileType, reportFilter);
            } catch (error) {
                console.error(error);
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }

            // End loading
            this.isDownloadingReportData = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.toggle-report-view-button :deep(.q-btn__wrapper) {
    padding: 6px;
}
</style>
